import React, { useRef, useState } from "react";
import { Icon } from "../Icon/Icon";
import { formValidation } from "../../helpers/formValidation";
import {
  convertInputGroupValuesToString,
  saveInputValueIntoState,
} from "../../helpers/otpInput";
import { isPhoneNumbersOnly } from "../../helpers/Pipes";

// Handle events are passed as ...props, to be managed by the parent component
export const Input = ({
  customClasses,
  inputType,
  name,
  label,
  value,
  valueLength,
  iconName,
  error,
  description,
  otpOnChange,
  otpOnKeyDown,
  onClickDescription,
  maxLength = "999",
  inputCustomClasses = "",
  _onFocus = () => {},
  _onBlur = () => {},
  ...props
}) => {
  const [otpValue, setOtpValue] = useState(Array(valueLength).fill(""));

  const fieldsetRef = useRef();
  const inputRef = useRef();

  const _inputType = inputType === "isPhoneNumber" ? "--cell" : "";
  const _otp = name === "otp" ? "--otp" : "";
  const _reddito = name === "reddito" ? "--cash" : "";
  const classes = [_inputType, _otp, _reddito].join(" ");

  // functions to manage otp inputs
  function handleOtpInputFocus(event) {
    event.target.focus();
  }

  function handleOtpFieldKeyDown(event, index) {
    const value = event.target.value;
    if (
      event.keyCode !== 13 &&
      (value !== "" || event.keyCode !== 8 || event.keyCode !== 9)
    ) {
      // set the values
      let newOtpValue = [...otpValue];
      newOtpValue[index] = "";
      setOtpValue(newOtpValue);

      // remove styles
      event.target.classList.remove("filled");
      event.target.classList.remove("wrong");
      fieldsetRef.current.classList.remove("is--error");
    }

    if (otpOnKeyDown) {
      otpOnKeyDown(event);
    }
  }

  function handleOtpFieldChange(event, index) {
    // at every change, validate the value of each input to set style and errors
    // must accept just numbers and just only one
    validateInputField(event);

    // then, get the values and save it into a state to be displayed on screen
    saveInputValueIntoState(event, index, otpValue, setOtpValue);

    if (otpOnChange) {
      otpOnChange(event);
    }
  }

  function validateInputField(event) {
    if (event.target.name === "otp") {
      const isValid = formValidation(event, 1);
      // set validations
      if (isValid) {
        // if it passes, set style props for succes
        event.target.classList.add("filled");
        event.target.classList.remove("wrong");

        // set the focus to the next field
        event.target.nextElementSibling.focus();
      } else {
        // if it fails, set style props for errors
        event.target.classList.add("wrong");
        event.target.classList.remove("filled");
        // keep the focus on the current field
        event.target.focus();
      }
    }
  }

  const forceOnlyNumbers = (event) => {
    const res = isPhoneNumbersOnly(event.key);
    if (!res) event.preventDefault();
    return res;
  };

  // functions to manage other inputs
  function handleActiveClassOnClick(event) {
    if (
      inputRef.current === event.target ||
      inputRef.current.value.length > 0
    ) {
      fieldsetRef.current.focus();
      fieldsetRef.current.classList.add("active");
    }

    _onFocus(event);
  }

  function handleActiveClassOnBlur(event) {
    if (
      inputRef.current === event.target &&
      inputRef.current.value.length !== 0
    ) {
      fieldsetRef.current.classList.add("active");
    } else {
      fieldsetRef.current.classList.remove("active");
    }

    _onBlur(event);
  }

  function renderExtraContent(name) {
    let content = "";

    switch (name) {
      case "phoneNumber":
        content = (
          <figure>
            <span className="fi fi-it">
                <svg xmlns='http://www.w3.org/2000/svg' id='flag-icons-it' viewBox='0 0 640 480'><g fill-rule='evenodd' stroke-width='1pt'><path fill='#fff' d='M0 0h640v480H0z'/><path fill='#009246' d='M0 0h213.3v480H0z'/><path fill='#ce2b37' d='M426.7 0H640v480H426.7z'/></g></svg>
              </span>
            <figcaption>+ 39</figcaption>
          </figure>
        );
        return content;

      case "reddito":
        content = <span className="cash-symbol">&euro;</span>;
        return content;

      default:
        return null;
    }
  }

  function setTypeOfInput(name) {
    switch (name) {
      case "otp":
        return "text";
      case "date":
        return "date";
      case "phoneNumber":
        return "number";
      default:
        return "text";
    }
  }

  return (
    <div className={["container-data-fieldset", customClasses].join(" ")}>
      <fieldset
        className={
          name === "otp"
            ? ["input-container--otp", classes].join(" ")
            : ["input-container", classes].join(" ")
        }
        ref={fieldsetRef}
      >
        {renderExtraContent(name)}

        {name === "otp" ? (
          <>
            {otpValue.map((otp, index) => (
              <input
                key={index}
                name="otp"
                id={`otp${index}`}
                type={setTypeOfInput(name)}
                className="otp-value"
                value={otpValue[index]}
                placeholder="&nbsp;"
                tabIndex={index + 1}
                inputMode="numeric" // does not work on old browsers
                data-validate="isMultipleNumber"
                onClick={(event) => handleOtpInputFocus(event)}
                onChange={(event) => handleOtpFieldChange(event, index)}
                onKeyDown={(event) => handleOtpFieldKeyDown(event, index)}
                required
              />
            ))}

            {/* set an input to hold the values from the otp */}
            <input
              name="otp"
              id="otp"
              type="hidden"
              value={convertInputGroupValuesToString(otpValue)}
              data-validate="isMultipleNumber"
            />
          </>
        ) : (
          <>
            <input
              id={name}
              name={name}
              type={setTypeOfInput(name)}
              value={value}
              data-validate={inputType}
              placeholder="&nbsp;"
              ref={inputRef}
              onFocus={(event) => handleActiveClassOnClick(event)}
              onBlur={(event) => handleActiveClassOnBlur(event)}
              onKeyDown={(event) =>
                "isPhoneNumber" == inputType
                  ? (event) => forceOnlyNumbers(event)
                  : ""
              }
              onInput={(e) => {
                if (
                  "isCodFiscale" == inputType ||
                  "isPhoneNumber" == inputType
                ) {
                  e.target.value = e.target.value.slice(0, maxLength);
                } else {
                  e.target.value = e.target.value;
                }
              }}
              maxLength={maxLength}
              className={inputCustomClasses}
              {...props}
            />
            <label htmlFor={name}>{label}</label>
            {iconName && <Icon setviewBox="yes" name={iconName} />}
          </>
        )}
      </fieldset>

      <aside className="box-info-error">
        <Icon setviewBox="yes" name="info" />
        <p>{error}</p>
      </aside>

      {description && <p className="more-info">{description}</p>}
    </div>
  );
};
